import { useMemo, useState } from "react";

/**
 * useSearchSort — a tiny, reusable local search + sort hook for listing pages.
 *
 * Usage:
 *   const items = [{ id, title, counterparty, created_at, value_amount, ... }, ...];
 *   const searchSort = useSearchSort(items, {
 *     searchKeys: ["title", "counterparty", "tags"],
 *     sortOptions: [
 *       { value: "updated_at", label: "Last updated", defaultDir: "desc" },
 *       { value: "title",      label: "Name A→Z",    defaultDir: "asc"  },
 *       { value: "value_amount", label: "Value",      defaultDir: "desc" },
 *     ],
 *   });
 *   // searchSort.items → filtered + sorted items
 *   // searchSort.bar   → drop-in <SearchSortBar /> JSX
 *
 * Designed to be drop-in — never touches the server.
 */
export default function useSearchSort(items, {
  searchKeys = [],
  sortOptions = [],
  defaultSort,
  storageKey,
} = {}) {
  const [q, setQ] = useState("");
  const initialSort = defaultSort || sortOptions[0]?.value;
  const initialDir = sortOptions.find(o => o.value === initialSort)?.defaultDir || "desc";
  const [sortBy, setSortBy] = useState(initialSort);
  const [sortDir, setSortDir] = useState(initialDir);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let out = items || [];
    if (query) {
      out = out.filter(item =>
        searchKeys.some(k => {
          const v = getByPath(item, k);
          if (v == null) return false;
          if (Array.isArray(v)) return v.some(x => String(x).toLowerCase().includes(query));
          return String(v).toLowerCase().includes(query);
        })
      );
    }
    if (sortBy) {
      const dir = sortDir === "asc" ? 1 : -1;
      out = [...out].sort((a, b) => {
        const av = getByPath(a, sortBy);
        const bv = getByPath(b, sortBy);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        const as = String(av).toLowerCase();
        const bs = String(bv).toLowerCase();
        if (as < bs) return -1 * dir;
        if (as > bs) return 1 * dir;
        return 0;
      });
    }
    return out;
  }, [items, q, searchKeys, sortBy, sortDir]);

  return {
    q, setQ,
    sortBy, setSortBy,
    sortDir, setSortDir,
    sortOptions,
    items: filtered,
    total: items?.length || 0,
    matched: filtered.length,
    storageKey,
  };
}

function getByPath(obj, path) {
  if (!obj) return undefined;
  if (!path) return undefined;
  if (path.indexOf(".") < 0) return obj[path];
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
}
